import _isArray from 'lodash/isArray';
import _isNil from 'lodash/isNil';
import _pickBy from 'lodash/pickBy';
import * as yup from 'yup';

export const doesFilterHaveValue = (item: any, key: string) => {
  if (key === 'custom_attributes') {
    return item?.[0]?.criteria?.length > 0;
  }

  const criteria =
    item?.[0]?.criteria?.[0] || item?.[0]?.criteria || item?.criteria?.[0] || item?.criteria;

  const criteriaHasValue =
    (_isArray(criteria) && criteria.length > 0) ||
    criteria?.values?.length > 0 ||
    criteria?.values === true ||
    criteria?.data?.length > 0 ||
    !(_isNil(criteria?.eq) || (_isArray(criteria?.eq) && criteria?.eq?.length === 0)) ||
    (!_isNil(criteria?.value) && criteria?.value !== '') ||
    criteria?.loyalty_tier_ids?.length > 0 ||
    criteria?.json_values?.length > 0 ||
    (!_isNil(criteria?.gteq) && !_isNil(criteria?.lteq));

  return criteriaHasValue;
};

// Per-field validation messages for the custom-attribute filter. Kept as plain strings
// because the consuming Formik schemas (Audience/Loyalty/Rule/Cashback) are not i18n-migrated.
export const CUSTOM_ATTRIBUTE_KEY_NAME_REQUIRED = 'Key name is required';
export const CUSTOM_ATTRIBUTE_OPERATOR_REQUIRED = 'Operator is required';
export const CUSTOM_ATTRIBUTE_VALUE_REQUIRED = 'Value is required';

// A custom-attribute criteria row is only valid to persist when key name, operator and
// value are all present. Blank rows persist `operator: null` and crash the backend
// loyalty-tier filter (PLA-7230), so every save/validation path must reject them.
// `false` (boolean radio) and `0` are valid values, so guard only for empty states.
const hasCustomAttribute = (attribute: any): boolean =>
  typeof attribute === 'string' ? attribute.trim().length > 0 : attribute != null;

const hasCustomOperator = (operator: any): boolean => operator != null && operator !== '';

const hasCustomValue = (value: any): boolean =>
  value != null && value !== '' && !(_isArray(value) && value.length === 0);

export const isCustomCriteriaComplete = (criterion: any): boolean => {
  if (!criterion) return false;

  return (
    hasCustomAttribute(criterion.attribute) &&
    hasCustomOperator(criterion.operator) &&
    hasCustomValue(criterion.selected_values)
  );
};

// Drops incomplete custom-attribute rows before persistence. When no complete row
// remains, the group is emptied so `doesFilterHaveValue` removes it entirely.
export const stripIncompleteCustomCriteria = (filters: any) => {
  const group = filters?.custom_attributes?.[0];
  if (!group) return filters;

  const criteria = (group.criteria || []).filter(isCustomCriteriaComplete);

  return {
    ...filters,
    custom_attributes: criteria.length ? [{ ...group, criteria }] : []
  };
};

// Single entry point for every filter save path: strip incomplete custom rows, then
// drop empty filters via the existing predicate.
export const sanitizeFilters = (filters: any) =>
  _pickBy(stripIncompleteCustomCriteria(filters), doesFilterHaveValue);

// Shared yup fragment for the Formik audience forms (Audience, Loyalty, Rule, Cashback).
// Errors attach to the leaf fields (attribute/operator/selected_values) so the shared
// Personal.tsx FItem controls render them inline, rather than to the criterion object.
export const customAttributesFilterSchema = yup
  .array()
  .nullable()
  .of(
    yup.object({
      criteria: yup
        .array()
        .nullable()
        .of(
          yup.object({
            attribute: yup
              .mixed()
              .test('key-name-required', CUSTOM_ATTRIBUTE_KEY_NAME_REQUIRED, hasCustomAttribute),
            operator: yup
              .mixed()
              .test('operator-required', CUSTOM_ATTRIBUTE_OPERATOR_REQUIRED, hasCustomOperator),
            selected_values: yup
              .mixed()
              .test('value-required', CUSTOM_ATTRIBUTE_VALUE_REQUIRED, hasCustomValue)
          })
        )
    })
  );

const hasRangeBound = (value: any): boolean => value === 0 || (value != null && value !== '');

// Innermost criteria object across the (many) filter shapes, mirroring doesFilterHaveValue.
const getInnerCriteria = (item: any): any =>
  item?.[0]?.criteria?.[0] || item?.[0]?.criteria || item?.criteria?.[0] || item?.criteria;

// A range is "partially filled" when exactly one of gteq/lteq is set. Safe fallback
// for dynamic keys we cannot classify: an empty range is left alone, a half range blocks.
const isPartiallyFilledRange = (item: any): boolean => {
  const criteria = getInnerCriteria(item);
  if (!criteria || typeof criteria !== 'object' || _isArray(criteria)) return false;
  if (!('gteq' in criteria) && !('lteq' in criteria)) return false;
  const hasGteq = hasRangeBound(criteria.gteq);
  const hasLteq = hasRangeBound(criteria.lteq);
  return (hasGteq || hasLteq) && !(hasGteq && hasLteq);
};

// age groups (FieldArray): every group needs both bounds; '' counts as empty.
const isAgeFilterComplete = (value: any): boolean => {
  const groups = _isArray(value) ? value : [];
  if (!groups.length) return false;
  return groups.every((group: any) => {
    const criteria = group?.criteria || {};
    return hasRangeBound(criteria.gteq) && hasRangeBound(criteria.lteq);
  });
};

// custom_attributes: enabled-with-no-row is incomplete; any present row must be complete.
const isCustomAttributesFilterComplete = (value: any): boolean => {
  const rows = value?.[0]?.criteria || [];
  if (!rows.length) return false;
  return rows.every(isCustomCriteriaComplete);
};

const LOCATION_FILTER_KEYS = ['city', 'states'];
const BIRTHDAY_COMPANION_KEYS = ['current_birthmonth', 'current_birthday'];
const BIRTHDAY_FILTER_KEYS = ['birthmonth', ...BIRTHDAY_COMPANION_KEYS];

// Standalone select/value filters where "enabled but empty" should block.
// doesFilterHaveValue already encodes "has a usable value" for each of these shapes.
const STANDALONE_FILTER_KEYS = [
  'gender',
  'race',
  'earned_badges',
  'campaign_enrolment',
  'earned_rewards',
  'loyalty_tier',
  'loyalty_points_balance',
  'leaderboard_position',
  'email_tags'
];

const isBirthdayGroupComplete = (filters: any): boolean => {
  const monthsFilled = doesFilterHaveValue(filters.birthmonth, 'birthmonth');
  const companionOn = BIRTHDAY_COMPANION_KEYS.some(
    key => filters[key]?.[0]?.criteria?.[0]?.values === true
  );
  return monthsFilled || companionOn;
};

// Returns the enabled-but-incomplete filter group keys. Composite groups collapse to
// a single key ('location' / 'birthmonth').
export const getIncompleteFilterKeys = (filters: any): string[] => {
  if (!filters || typeof filters !== 'object') return [];
  const incomplete: string[] = [];
  const isPresent = (key: string) => filters[key] != null;

  if (LOCATION_FILTER_KEYS.some(isPresent)) {
    const anyFilled = LOCATION_FILTER_KEYS.some(
      key => isPresent(key) && doesFilterHaveValue(filters[key], key)
    );
    if (!anyFilled) incomplete.push('location');
  }

  if (BIRTHDAY_FILTER_KEYS.some(isPresent) && !isBirthdayGroupComplete(filters)) {
    incomplete.push('birthmonth');
  }

  Object.keys(filters).forEach(key => {
    if (filters[key] == null) return;
    if (LOCATION_FILTER_KEYS.includes(key) || BIRTHDAY_FILTER_KEYS.includes(key)) return;

    if (key === 'age') {
      if (!isAgeFilterComplete(filters[key])) incomplete.push(key);
    } else if (key === 'custom_attributes') {
      if (!isCustomAttributesFilterComplete(filters[key])) incomplete.push(key);
    } else if (STANDALONE_FILTER_KEYS.includes(key)) {
      if (!doesFilterHaveValue(filters[key], key)) incomplete.push(key);
    } else if (isPartiallyFilledRange(filters[key])) {
      // Unknown/dynamic key: only a half-filled range blocks.
      incomplete.push(key);
    }
  });

  return incomplete;
};

export const FILTER_GROUP_LABELS: Record<string, string> = {
  age: 'Age',
  birthmonth: 'Birthday',
  gender: 'Gender',
  race: 'Ethnicity',
  location: 'Location',
  city: 'City',
  states: 'State',
  earned_badges: 'Badge',
  campaign_enrolment: 'Campaign Enrolment',
  leaderboard_position: 'Leaderboard Position',
  loyalty_points_balance: 'Loyalty Points Balance',
  loyalty_tier: 'Loyalty Tier',
  account_confirmed_at: 'Member Since',
  earned_rewards: 'Reward',
  email_tags: 'Email Tags',
  custom_attributes: 'Custom Attributes'
};

const humanizeFilterKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

export const getIncompleteFilterLabels = (filters: any): string[] =>
  getIncompleteFilterKeys(filters).map(key => FILTER_GROUP_LABELS[key] || humanizeFilterKey(key));

export const INCOMPLETE_FILTERS_MESSAGE_PREFIX =
  'Please complete or remove these audience filters before proceeding: ';

export const getIncompleteFiltersMessage = (filters: any): string | null => {
  const labels = getIncompleteFilterLabels(filters);
  return labels.length ? `${INCOMPLETE_FILTERS_MESSAGE_PREFIX}${labels.join(', ')}.` : null;
};
